<template>
  <div
    v-if="applicationReady"
    class="who-are-you-screening"
  >
    <div class="questionnaire-top">
      <b-row v-show="msg">
        <b-col
          cols="12"
          md="8"
          offset-md="2"
          class="page-message"
        >
          <h3>
            {{ title }}
          </h3>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col class="text-center">
          Start typing to find your name.<br>
          Can't find your name? <a
            href="#"
            @click="reloadVisitorList"
          >Click Here</a> to reload the visitor list.
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          md="8"
          offset-md="2"
        >
          <vue-typeahead-bootstrap
            v-model="selectedName"
            @hit="setSelectedUser"
            :data="[...allBfmsWithBranchUsers, ...newVisitors]"
            :serializer="user => user.searchData"
            :max-matches="40"
            input-class="type-ahead"
          />
        </b-col>
      </b-row>

      <b-row
        v-if="showSimulateBranchId"
      >
        <b-col
          cols="2"
          offset="5"
          justify-content-center
        >
          <b-form-input
            size="lg"
            type="number"
            v-model="simulatedBranchId"
            @keyup.enter="setSimulatedBranchId"
            style="display: flex, width: 100px;"
          />
        </b-col>
      </b-row>

      <b-row
        v-if="showSimulateState"
      >
        <b-col
          cols="2"
          offset="5"
          justify-content-center
        >
          <b-form-input
            size="lg"
            v-model="simulatedState"
            @keyup.enter="setSimulatedState"
            style="display: flex, width: 100px;"
          />
        </b-col>
      </b-row>
    </div>

    <div class="questionnaire-bottom">
      <b-row>
        <b-col cols="12">
          <b-form>
            <b-form-row>
              <b-col
                cols="12"
                md="5"
                class="pb-3 d-flex flex-column justify-content-end text-center"
              >
                <div class="text-center text-md-left pb-2">
                  <b-link
                    to="/first-time-visitor"
                    class="first-time-visitor"
                  >
                    Add first time visitor &raquo;
                  </b-link>
                </div>
              </b-col>
              <b-col
                cols="12"
                sm="4"
                md="2"
                class="pb-3 d-flex flex-row justify-content-center justify-content-sm-end"
              >
                <b-button
                  @click="nextPage"
                  :disabled="canGoForward"
                  class="btn-green"
                >
                  Submit
                </b-button>
              </b-col>
            </b-form-row>
          </b-form>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
// import store from '@/store';

export default {
  name: 'WhoAreYouScreening',
  components: {
    'vue-typeahead-bootstrap': () => import(/* webpackPrefetch: true */ /* webpackChunkName: "vue-typeahead-bootstrap" */ 'vue-typeahead-bootstrap')
  },
  props: {
    msg: {
      type: String,
      default: 'Who are you screening?'
    }
  },
  data () {
    return {
      selectedName: '',
      searchDatabase: [],
      // simulatedBranchId: '',
      showSimulateBranchId: false,
      simulatedState: '',
      showSimulateState: false
    }
  },
  mounted () {
    this.clearSelectedUser()
    this.clearLastScreening()
    this.clearPageAlertInformation()
    this.setSelectedQuestionnaireId(0)
    this.setFormSubmitted(false)
    this.setFailedScreening(false)
    this.setFailureDescription('')
    this.checkNumberOfflineInvalidForms()
  },
  computed: {
    ...mapGetters([
      'applicationReady',
      'selectedUser'
    ]),
    ...mapGetters({
      allBfmsWithBranchUsers: 'branchUsers/allBfmsWithBranchUsers',
      newVisitors: 'newVisitors',
      user: 'authentication/user',
      numberOfflineInvalidForms: 'offline/numberInvalidForms'
    }),
    simulatedBranchId () {
      return this.user.primaryBranchId
    },
    canGoForward () {
      return this.isEmptyObject(this.selectedUser)
    },
    title () {
      if (this.isSelfMode) {
        return 'Who are you?'
      }
      return this.msg
    }
  },
  watch: {
    numberOfflineInvalidForms (newValue, oldValue) {
      this.checkNumberOfflineInvalidForms()
    },
    async selectedName (newValue, oldValue) {
      const newValueLowerCase = newValue.toLowerCase()
      if (newValueLowerCase === 'turn on retry failure') {
        this.turnOnSimulatedRetryFailure()
      }
      if (newValueLowerCase === 'turn off retry failure') {
        this.turnOffSimulatedRetryFailure()
      }
      if (newValueLowerCase === 'view diag') {
        this.$router.push({ name: 'diagnostics' })
      }
      if (newValueLowerCase === 'set branch id') {
        this.showSimulateBranchId = true
      } else if (oldValue === 'set branch id') {
        this.showSimulateBranchId = false
      }
      if (newValueLowerCase === 'reset branch id') {
        this.resetBranchId()
        this.selectedName = ''
        this.simulatedBranchId = this.user.primaryBranchId
      }
      if (newValueLowerCase === 'set state') {
        this.showSimulateState = true
      } else if (oldValue === 'set state') {
        this.showSimulateState = false
      }
      if (newValueLowerCase === 'reset state') {
        this.resetState()
        this.selectedName = ''
        this.simulatedState = this.user.state
      }
      if (newValueLowerCase === 'send diag') {
        await this.submitDiagnostics()
        const alertData = {
          variant: 'warning',
          message: 'Diagnostics',
          alerts: [
            'BETS-e app has sent diagnostics to Branch Support.',
            'Let us review the information to identify the issue.'
          ]
        }
        this.setPageAlertInformation(alertData)
        this.selectedName = ''
      }
      if (newValueLowerCase === 'check for update') {
        await this.checkForUpdate()
        this.selectedName = ''
      }
      if (newValueLowerCase === 'clear logs') {
        await this.clearLogs()
        this.selectedName = ''
      }
      if (newValueLowerCase === 'show about') {
        this.setShowAboutModal(true)
        this.selectedName = ''
      }
    }
  },
  methods: {
    ...mapActions([
      'setSelectedUser',
      'clearSelectedUser',
      'clearPageAlertInformation',
      'clearLastScreening',
      'setShowAboutModal'
    ]),
    ...mapActions({
      setSelectedQuestionnaireId: 'forms/setSelectedQuestionnaireId',
      setSearchAllBfms: 'branchUsers/setSearchAllBfms',
      fetchBranchUsers: 'branchUsers/fetchBranchUsers',
      setFormSubmitted: 'forms/setFormSubmitted',
      setFailedScreening: 'forms/setFailedScreening',
      setFailureDescription: 'forms/setFailureDescription',
      turnOnSimulatedRetryFailure: 'offline/turnOnSimulatedRetryFailure',
      turnOffSimulatedRetryFailure: 'offline/turnOffSimulatedRetryFailure',
      simulateBranchId: 'authentication/simulateBranchId',
      resetBranchId: 'authentication/resetBranchId',
      simulateState: 'authentication/simulateState',
      resetState: 'authentication/resetState',
      submitDiagnostics: 'support/submitDiagnostics',
      clearLogs: 'support/clearLogs'
    }),
    async reloadVisitorList () {
      await this.$store.dispatch('setOverlayMessage', 'Loading Visitors list. This may take several seconds...')
      await this.$store.dispatch('setLoading', true)
      const user = this.$store.state.authentication.user
      const branchUserObj = {
        branch_id: user.C_branch_item_id_primary,
        user_id: user.item_id
      }
      await this.$store.dispatch('branchUsers/fetchBranchUsers', branchUserObj)
      await this.$store.dispatch('setLoading', false)
    },
    nextPage () {
      if ((this.selectedUser.type === 'visitors') && (this.selectedUser.visitorType === null || this.selectedUser.visitorType === '')) {
        this.$router.push({ name: 'set-visitor-type' })
      } else if (this.isSelfMode) {
        this.$router.push({ name: 'visitor-guidelines' })
      } else {
        this.$router.push({ name: 'available-forms' })
      }
    },
    checkNumberOfflineInvalidForms () {
      if (this.numberOfflineInvalidForms > 0 && this.isOnline) {
        const alertData = {
          variant: 'danger',
          message: 'Send Error',
          type: 'erroroptions',
          alerts: [
            this.numberOfflineInvalidForms + ' screenings could not be sent. Please contact Branch Support or '
          ]
        }
        this.setPageAlertInformation(alertData)
      } else if (this.numberOfflineInvalidForms === 0 && this.isOnline) {
        this.clearPageAlertInformation()
      }
    },
    setSimulatedBranchId () {
      this.showSimulateBranchId = false
      this.selectedName = ''
      this.simulateBranchId(this.simulatedBranchId)
    },
    setSimulatedState () {
      this.showSimulateState = false
      this.selectedName = ''
      this.simulateState(this.simulatedState)
    }
  }
}
</script>

<style scoped lang="scss">
.questionnaire-top {
  height: 50vh;
}

.questionnaire-bottom {
  height: 30vh;
  text-align: center;
}

.page-message {
  text-align: center;
  margin-bottom: .5em;
}

.first-time-visitor {
  a {
    font-weight: 500;
  }
}

.question-navigation {
  a:link, a:visited, a:hover {
    color: #636466;
  }
}

@media (max-width: 413px) {
  .question-navigation btn {
    margin: 8px;
  }
  .first-time-visitor {
    width: 160px;
  }
}
</style>
